import { Button } from "@component/button";
import { ClockIcon, SettingsIcon, StarIcon, TrendingUpIcon, WifiIcon } from "lucide-react";

interface InterviewSidebarProps {
    accentColor?: string;
    showBranding?: boolean;
    timeRemaining: string;
    onStartInterview?: () => void;
    interviewLanguage?: string;
}

export default function PreviewInterviewSidebar({
    accentColor = "#2563eb",
    showBranding = true,
    timeRemaining,
    onStartInterview,
    interviewLanguage = "en-US",
}: InterviewSidebarProps) {
    const isMalay = interviewLanguage === "Malay";
    const isIndonesian = interviewLanguage === "id-ID";

    const t = (en: string, id: string, ms: string) => {
        if (isMalay) return ms;
        if (isIndonesian) return id;
        return en;
    };

    return (
        <div className="max-w-[440px] p-5">
            <div>
                <h2 className="text-2xl md:text-xl font-semibold mt-0 sm:mt-4 md:mt-0 sm:mb-4">
                    {t(
                        "Get ready for your AI interview",
                        "Persiapkan diri Anda untuk wawancara AI",
                        "Sedia untuk temuduga AI anda"
                    )}
                </h2>

                <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                        <StarIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            {t(
                                "Start now or come back later",
                                "Mulai sekarang atau kembali lagi nanti",
                                "Mula sekarang atau kembali kemudian"
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <TrendingUpIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            {t(
                                "Stand out beyond a CV and get noticed faster",
                                "Tampilkan potensi terbaik Anda, bukan hanya dari CV",
                                "Tonjolkan diri melebihi CV dan dikenali lebih cepat"
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <SettingsIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            {t(
                                "Check your device settings",
                                "Periksa pengaturan perangkat Anda",
                                "Semak tetapan peranti anda"
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <WifiIcon className="h-4 w-4 text-[#2563eb] flex-shrink-0" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            {t(
                                "Find a quiet place with stable internet",
                                "Temukan tempat yang tenang dengan koneksi internet stabil",
                                "Cari tempat yang tenang dengan internet yang stabil"
                            )}
                        </span>
                    </div>
                </div>

                {/* AI Bot Avatar */}
                <div className="mb-4 flex flex-col items-center">
                    <p className="text-sm text-gray-600 mb-2">
                        {t(
                            "AI Interviewer is waiting",
                            "Pewawancara AI sedang menunggu",
                            "Penemuduga AI sedang menunggu"
                        )}
                    </p>
                    <div className="flex items-center justify-center space-x-1 text-sm text-amber-500">
                        <ClockIcon className="w-4 h-4" />
                        <span>{t("Join within", "Bergabung dalam", "Sertai dalam")}: {timeRemaining}</span>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="w-full space-y-3 sm:space-y-2 flex flex-col items-center mt-4 sm:mt-0">
                    <div className="w-full relative">
                        <Button
                            className={`w-full text-white py-7 px-20 text-lg sm:text-base sm:py-6 rounded-full transition-opacity bg-blue-600 hover:bg-blue-700"} cursor-pointer`}
                            style={{ backgroundColor: showBranding ? accentColor : "#2563eb" }}
                            onClick={onStartInterview}
                        >
                            {t("Start Interview", "Mulai Wawancara", "Mula Temuduga")}
                        </Button>
                    </div>
                </div>

                <p className="text-xs text-gray-500 mt-4 text-center">
                    {t(
                        "ourteam uses generative AI to conduct the AI interview.",
                        "ourteam menggunakan AI generatif untuk melakukan wawancara ini.",
                        "ourteam menggunakan generatif AI untuk menjalankan temuduga AI ini."
                    )}
                    <br />
                    {t(
                        "Your responses are used only to assess your candidacy and are never used to train AI models.",
                        "Respons Anda hanya akan digunakan untuk menilai kelayakan sebagai kandidat dan tidak akan digunakan untuk melatih model AI.",
                        "Respons anda hanya digunakan untuk menilai kelayakan pencalonan anda dan tidak akan digunakan untuk melatih model AI."
                    )}
                </p>
            </div>
        </div>
    );
}
